import { focusCalendarAgenda } from '../actions/focusCalendarAgenda';
import { focusCalendarSurface } from '../actions/focusCalendarSurface';
import { goToToday } from '../actions/goToToday';
import { invokeNewEventByKeyboard } from '../actions/invokeNewEventByKeyboard';
import { openPrintDialog } from '../actions/openPrintDialog';
import { isFocusAtStackRoot } from 'owa-calendar-focus-manager';
import { getCommands } from 'owa-calendar-hotkeys';
import { getIsAgendaPaneShown, toggleAgendaPane } from 'owa-calendar-layout';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { CalendarViewTypeEnum } from 'owa-calendar-view-type';
import { DateRange, getNextDateRange, getPreviousDateRange } from 'owa-datetime-utils';
import { useGlobalHotkey } from 'owa-hotkeys';

// TODO: VSO #88707 Separate module vs surface concerns in Calendar module
// This file will be further decomposed in upcoming iterations to fully separate surface and canvas
// Additionally, some hotkeys will need to be conditionally enabled based on view type (for now, they are no-ops)
// We should also add a hotkey for switching to canvas view

let hotkeysEnabled: boolean = true;

export function useCalendarModuleContentHotkeys(
    isSurfaceViewType: boolean,
    onViewTypeChange: (
        newCalendarViewType: CalendarViewTypeEnum,
        newNumOfDaysInDayRange?: number
    ) => void,
    onSurfaceDateRangeChange: (newDateRange: DateRange) => void
) {
    const commands = getCommands();

    // default options are: preventDefault: true, stopPropagation: true, allowHotkeyOnTextFields: false
    useGlobalHotkey(commands.dayView, dayView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.weekView, weekView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.workWeekView, workWeekView, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });
    useGlobalHotkey(commands.monthView, monthView, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.agendaMonthView, agendaMonthView, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });

    useGlobalHotkey(commands.newEvent, newEvent, { isEnabled });
    useGlobalHotkey(commands.nextDateRange, nextDateRange, { isEnabled });
    useGlobalHotkey(commands.prevDateRange, prevDateRange, { isEnabled });
    useGlobalHotkey(commands.goToToday, onGoToToday, { allowHotkeyOnTextFields: true, isEnabled });
    useGlobalHotkey(commands.printCalendar, printCalendar, {
        allowHotkeyOnTextFields: true,
        isEnabled,
    });

    function dayView() {
        onViewTypeChange(CalendarViewTypeEnum.Daily);
        focusCalendarSurface();
    }

    function workWeekView() {
        onViewTypeChange(CalendarViewTypeEnum.WorkWeek);
        focusCalendarSurface();
    }

    function weekView() {
        onViewTypeChange(CalendarViewTypeEnum.Weekly);
        focusCalendarSurface();
    }

    function monthView() {
        onViewTypeChange(CalendarViewTypeEnum.Monthly);
        focusCalendarSurface();
    }

    function agendaMonthView() {
        onViewTypeChange(CalendarViewTypeEnum.Monthly);
        if (!getIsAgendaPaneShown()) {
            toggleAgendaPane();
        }
        focusCalendarAgenda();
    }

    function newEvent() {
        invokeNewEventByKeyboard();
    }

    function nextDateRange() {
        if (isSurfaceViewType) {
            let newDateRange = getNextDateRange(surfaceStore.dateRange, surfaceStore.dateRangeType);
            onSurfaceDateRangeChange(newDateRange);
            focusCalendarSurface();
        }
    }

    function prevDateRange() {
        if (isSurfaceViewType) {
            let newDateRange = getPreviousDateRange(
                surfaceStore.dateRange,
                surfaceStore.dateRangeType
            );
            onSurfaceDateRangeChange(newDateRange);
            focusCalendarSurface();
        }
    }

    function onGoToToday() {
        if (isSurfaceViewType) {
            goToToday();
            focusCalendarSurface();
        }
    }

    function printCalendar() {
        if (isSurfaceViewType) {
            openPrintDialog(surfaceStore);
        }
    }
}

export function disableCalendarModuleContentHotkeys() {
    hotkeysEnabled = false;
}

export function enableCalendarModuleContentHotkeys() {
    hotkeysEnabled = true;
}

function isEnabled() {
    // we want to disable hotkeys if there are any fabric popups open, not just ones the surface owns, so check for those separately
    return hotkeysEnabled && isFocusAtStackRoot();
}
